<template>
  <div class="sas" dir="rtl">

    <!-- ===== טעינה ראשונה ===== -->
    <div v-if="loading && !stats" class="sas-loading" aria-live="polite">
      <v-skeleton-loader type="image" class="sas-loading__hero" />
      <v-skeleton-loader type="table-heading, list-item-two-line, list-item-two-line" />
    </div>

    <!-- ===== שגיאה ===== -->
    <div v-else-if="error && !stats" class="sas-message" role="alert">
      <p>{{ error }}</p>
      <v-btn color="#0d2c6d" dark depressed @click="load(false)">ניסיון נוסף</v-btn>
    </div>

    <!-- ===== אין סקרים בכלל ===== -->
    <div v-else-if="stats && !stats.totals.surveys" class="sas-message">
      <p>עוד לא נוצרו סקרים באף מערכת. כשבעלי מערכות ייצרו סקרים בשלוחה 11 או בממשק, הנתונים יופיעו כאן.</p>
    </div>

    <template v-else-if="stats">
      <!-- ===== Hero: לוח המקשים של כל ההצבעות ===== -->
      <section class="sas-hero" aria-labelledby="sas-hero-title">
        <div class="sas-hero__text">
          <h2 id="sas-hero-title" class="sas-hero__title">על איזה מקש המאזינים לוחצים</h2>
          <p class="sas-hero__headline">{{ headline }}</p>
          <p class="sas-hero__sub">
            לפי {{ fmt(stats.totals.votes) }} הצבעות ב-{{ fmt(stats.totals.surveys) }} סקרים של {{ fmt(stats.totals.systems) }} מערכות
          </p>
          <div class="sas-hero__meta">
            <span>עודכן {{ updatedAgo }}</span>
            <v-btn text small color="#fff" :loading="loading" @click="load(true)">
              <v-icon left small>mdi-refresh</v-icon>
              רענון
            </v-btn>
          </div>
        </div>

        <ol class="sas-pad" :class="{ 'is-ready': padReady }" aria-label="הצבעות לפי מקש">
          <li v-for="(k, i) in keypad" :key="k.label" class="sas-pad__key"
            :class="{ 'is-lead': k.isLead, 'is-off': k.off, 'is-empty': !k.off && !k.votes }"
            :style="{ '--fill': padReady ? k.fill : 0, '--delay': (i * 45) + 'ms' }"
            :aria-label="k.off ? null : ('מקש ' + k.label + ': ' + fmt(k.votes) + ' הצבעות' + (k.votes ? ', ' + k.shareLabel : ''))"
            :aria-hidden="k.off ? 'true' : null">
            <span class="sas-pad__digit">{{ k.label }}</span>
            <span v-if="!k.off" class="sas-pad__share" dir="ltr">{{ k.shareLabel }}</span>
          </li>
        </ol>
      </section>

      <!-- ===== המספרים ===== -->
      <dl class="sas-strip">
        <div v-for="item in strip" :key="item.label" class="sas-strip__item">
          <dt>{{ item.label }}</dt>
          <dd>
            <span class="sas-strip__value">{{ item.value }}</span>
            <span v-if="item.note" class="sas-strip__note">{{ item.note }}</span>
          </dd>
        </div>
      </dl>

      <!-- ===== פעילות לפי שבוע ===== -->
      <section class="sas-block" aria-labelledby="sas-weekly-title">
        <header class="sas-block__head">
          <h3 id="sas-weekly-title">פעילות ב-12 השבועות האחרונים</h3>
          <p class="sas-legend">
            <span class="sas-legend__bar"></span> הצבעות
            <span class="sas-legend__num" aria-hidden="true"></span> סקרים חדשים
          </p>
        </header>

        <div ref="weeks" class="sas-weeks" role="img" :aria-label="weeklySummary">
          <div v-for="w in weeklyBars" :key="w.weekStart" class="sas-week" :class="{ 'is-current': w.isCurrent }">
            <span class="sas-week__votes">{{ w.votes ? fmt(w.votes) : '' }}</span>
            <span class="sas-week__track">
              <span class="sas-week__bar" :style="{ height: w.height + '%' }"></span>
            </span>
            <span class="sas-week__new" :class="{ 'is-zero': !w.surveys }">{{ w.surveys }}</span>
            <span class="sas-week__date">{{ w.label }}</span>
          </div>
        </div>

        <!-- אותם נתונים כטבלה לקוראי מסך -->
        <table class="sas-sr-only">
          <caption>הצבעות וסקרים חדשים לפי שבוע</caption>
          <tr><th>שבוע שמתחיל ב-</th><th>הצבעות</th><th>סקרים חדשים</th></tr>
          <tr v-for="w in weeklyBars" :key="'t' + w.weekStart">
            <td>{{ w.label }}</td><td>{{ w.votes }}</td><td>{{ w.surveys }}</td>
          </tr>
        </table>
      </section>

      <!-- ===== מערכות מובילות ===== -->
      <div class="sas-boards">
        <section v-for="board in boards" :key="board.id" class="sas-block" :aria-labelledby="'sas-' + board.id">
          <header class="sas-block__head">
            <h3 :id="'sas-' + board.id">{{ board.title }}</h3>
          </header>
          <p v-if="!board.rows.length" class="sas-none">{{ board.empty }}</p>
          <ol v-else class="sas-board">
            <li v-for="(row, i) in board.rows" :key="row.ivrId" class="sas-board__row">
              <span class="sas-board__rank">{{ i + 1 }}</span>
              <span class="sas-board__who">
                <span class="sas-board__name">{{ row.name || row.lineNumber }}</span>
                <span class="sas-board__line">{{ row.lineNumber }}</span>
              </span>
              <span class="sas-board__track" aria-hidden="true">
                <span class="sas-board__bar" :style="{ width: barWidth(row[board.metric], board.max) }"></span>
              </span>
              <span class="sas-board__value">
                {{ fmt(row[board.metric]) }}
                <small>{{ board.unit }}</small>
              </span>
              <span class="sas-board__aside">{{ board.aside(row) }}</span>
            </li>
          </ol>
        </section>
      </div>

      <!-- ===== הסקרים עם הכי הרבה הצבעות ===== -->
      <section class="sas-block" aria-labelledby="sas-top-title">
        <header class="sas-block__head">
          <h3 id="sas-top-title">הסקרים עם הכי הרבה הצבעות</h3>
        </header>
        <p v-if="!stats.topSurveys.length" class="sas-none">עוד אין הצבעות בסקרים.</p>
        <ol v-else class="sas-top">
          <li v-for="s in stats.topSurveys" :key="s.id" class="sas-top__row">
            <div class="sas-top__main">
              <p class="sas-top__q">{{ s.question || ('סקר ' + s.integerId) }}</p>
              <p class="sas-top__meta">
                <span class="sas-pill" :class="'is-' + s.status">{{ statusLabel(s.status) }}</span>
                <span>{{ s.systemName }}</span>
                <span v-if="s.lineNumber" class="sas-top__line">{{ s.lineNumber }}</span>
                <span>{{ s.source === 'web' ? 'נוצר בממשק' : 'הוקלט בטלפון' }}</span>
                <span>{{ fmtDate(s.createdAt) }}</span>
              </p>
            </div>
            <span class="sas-top__keys" aria-hidden="true">
              <span v-for="k in miniKeys(s)" :key="k.key" class="sas-mini" :class="{ 'is-lead': k.isLead }"
                :style="{ '--fill': k.fill }">{{ k.key }}</span>
            </span>
            <span class="sas-top__votes">
              {{ fmt(s.votes) }}
              <small>הצבעות</small>
            </span>
          </li>
        </ol>
      </section>

      <!-- ===== איך בונים סקרים ===== -->
      <section class="sas-block" aria-labelledby="sas-mix-title">
        <header class="sas-block__head">
          <h3 id="sas-mix-title">איך בעלי המערכות בונים סקרים</h3>
        </header>
        <div class="sas-mix">
          <div v-for="row in mix" :key="row.title" class="sas-mix__row">
            <p class="sas-mix__title">{{ row.title }}</p>
            <div class="sas-split" role="img" :aria-label="row.aria">
              <span v-for="part in row.parts" :key="part.label" class="sas-split__part" :class="'is-' + part.tone"
                :style="{ flexGrow: part.n || 0 }"></span>
            </div>
            <p class="sas-mix__legend">
              <span v-for="part in row.parts" :key="'l' + part.label" class="sas-mix__item">
                <span class="sas-mix__dot" :class="'is-' + part.tone"></span>
                {{ part.label }} <b>{{ fmt(part.n) }}</b>
              </span>
            </p>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import ApiServices from '@/services/api.service'

const PAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const STATUS_LABELS = { open: 'פתוח', processing: 'מתוזמן', closed: 'סגור' };

export default {
  name: 'SurveysAdminStats',
  data: () => ({
    stats: null,
    loading: false,
    error: '',
    padReady: false,
    now: Date.now(),
  }),
  computed: {
    keyVotes() {
      const map = {};
      (this.stats ? this.stats.keys : []).forEach(k => { map[String(k.key)] = k.votes; });
      return map;
    },
    keypad() {
      const total = this.stats ? this.stats.totals.votes : 0;
      const max = Math.max(0, ...Object.values(this.keyVotes));
      return PAD.map(label => {
        const off = label === '*' || label === '#';
        const votes = off ? 0 : (this.keyVotes[label] || 0);
        return {
          label,
          off,
          votes,
          share: total ? Math.round((votes / total) * 100) : 0,
          // מקש עם מעט הצבעות לא מוצג כ-0%
          shareLabel: !votes ? '' : (Math.round((votes / total) * 100) === 0 ? '<1%' : Math.round((votes / total) * 100) + '%'),
          fill: max ? votes / max : 0,
          isLead: !off && max > 0 && votes === max,
        };
      });
    },
    headline() {
      const lead = this.keypad.filter(k => k.isLead);
      if (!lead.length) return 'עוד אין הצבעות בסקרים';
      if (lead.length > 1) return 'מקשים ' + lead.map(k => k.label).join(' ו-') + ' מובילים יחד, עם ' + lead[0].share + '% כל אחד';
      return 'מקש ' + lead[0].label + ' מקבל ' + lead[0].share + '% מכל ההצבעות';
    },
    updatedAgo() {
      if (!this.stats) return '';
      const minutes = Math.floor((this.now - new Date(this.stats.generatedAt).getTime()) / 60000);
      if (minutes < 1) return 'עכשיו';
      if (minutes === 1) return 'לפני דקה';
      return 'לפני ' + minutes + ' דקות';
    },
    strip() {
      const t = this.stats.totals;
      return [
        { label: 'סקרים', value: this.fmt(t.surveys), note: '+' + this.fmt(t.createdLast30) + ' ב-30 יום' },
        { label: 'פתוחים עכשיו', value: this.fmt(t.open), note: t.scheduled ? this.fmt(t.scheduled) + ' מתוזמנים' : '' },
        { label: 'מערכות עם סקרים', value: this.fmt(t.systems), note: '' },
        { label: 'הצבעות', value: this.fmt(t.votes), note: '+' + this.fmt(t.votesLast30) + ' ב-30 יום' },
        { label: 'ממוצע הצבעות לסקר', value: this.fmt(t.avgVotesPerSurvey), note: '' },
      ];
    },
    weeklyBars() {
      const weeks = this.stats.weekly;
      const max = Math.max(0, ...weeks.map(w => w.votes));
      return weeks.map((w, i) => ({
        ...w,
        label: this.fmtDay(w.weekStart),
        height: max ? Math.max(2, (w.votes / max) * 100) : 0,
        isCurrent: i === weeks.length - 1,
      }));
    },
    weeklySummary() {
      const votes = this.stats.weekly.reduce((a, w) => a + w.votes, 0);
      const surveys = this.stats.weekly.reduce((a, w) => a + w.surveys, 0);
      return 'ב-12 השבועות האחרונים: ' + this.fmt(votes) + ' הצבעות ו-' + this.fmt(surveys) + ' סקרים חדשים';
    },
    boards() {
      const bySurveys = this.stats.topSystemsBySurveys;
      const byVotes = this.stats.topSystemsByVotes;
      return [
        {
          id: 'by-surveys',
          title: 'המערכות עם הכי הרבה סקרים',
          metric: 'surveys',
          unit: 'סקרים',
          max: bySurveys.length ? bySurveys[0].surveys : 0,
          rows: bySurveys,
          empty: 'עוד אין סקרים.',
          aside: row => (row.open ? this.fmt(row.open) + ' פתוחים' : ''),
        },
        {
          id: 'by-votes',
          title: 'המערכות עם הכי הרבה הצבעות',
          metric: 'votes',
          unit: 'הצבעות',
          max: byVotes.length ? byVotes[0].votes : 0,
          rows: byVotes,
          empty: 'עוד אין הצבעות.',
          aside: row => this.fmt(row.surveys) + (row.surveys === 1 ? ' סקר' : ' סקרים'),
        },
      ];
    },
    mix() {
      const c = this.stats.composition;
      const t = this.stats.totals;
      const row = (title, parts) => ({
        title,
        parts,
        aria: title + ': ' + parts.map(p => p.label + ' ' + p.n).join(', '),
      });
      return [
        row('סטטוס', [
          { label: 'פתוחים', n: t.open, tone: 'open' },
          { label: 'מתוזמנים', n: t.scheduled, tone: 'scheduled' },
          { label: 'סגורים', n: t.closed, tone: 'closed' },
        ]),
        row('איפה נוצרו', [
          { label: 'בטלפון', n: c.phone, tone: 'ink' },
          { label: 'בממשק', n: c.web, tone: 'soft' },
        ]),
        row('זיהוי המצביעים', [
          { label: 'גלוי', n: c.named, tone: 'ink' },
          { label: 'אנונימי', n: c.anonymous, tone: 'soft' },
        ]),
        row('הצבעות לכל מאזין', [
          { label: 'הצבעה אחת', n: c.single, tone: 'ink' },
          { label: 'כמה הצבעות', n: c.multi, tone: 'soft' },
        ]),
      ];
    },
  },
  methods: {
    async load(refresh) {
      this.loading = true;
      this.error = '';
      try {
        const token = localStorage.getItem('token');
        const api = process.env.VUE_APP_BASE_URL + '/surveys/admin_stats' + (refresh ? '?refresh=1' : '');
        const res = await fetch(api, ApiServices.requestOptions('GET', '', token));
        if (res.status !== 200) {
          this.error = res.status === 403 ? 'המסך הזה זמין לאדמין בלבד.' : 'לא הצלחנו לטעון את הנתונים. נסו שוב בעוד רגע.';
          return;
        }
        this.padReady = false;
        this.stats = await res.json();
        this.now = Date.now();
        // הרגע היחיד של תנועה במסך: המקשים מתמלאים אחרי שהנתונים הגיעו
        this.$nextTick(() => {
          requestAnimationFrame(() => { this.padReady = true; });
          // במסך צר הגרף נגלל: מציגים קודם את השבוע הנוכחי (בצד שמאל ב-RTL)
          const box = this.$refs.weeks;
          if (box && box.scrollWidth > box.clientWidth) box.scrollLeft = -box.scrollWidth;
        });
      } catch (err) {
        this.error = 'לא הצלחנו לטעון את הנתונים: ' + err.message;
      } finally {
        this.loading = false;
      }
    },
    miniKeys(s) {
      const max = Math.max(0, ...s.keys.map(k => k.votes));
      return s.keys.map(k => ({ key: k.key, fill: max ? k.votes / max : 0, isLead: max > 0 && k.votes === max }));
    },
    barWidth(value, max) {
      return (max ? Math.max(3, (value / max) * 100) : 0) + '%';
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    fmt(n) {
      return Number(n || 0).toLocaleString('he-IL');
    },
    fmtDay(isoDate) {
      const [, m, d] = isoDate.split('-');
      return Number(d) + '.' + Number(m);
    },
    fmtDate(value) {
      return value ? new Date(value).toLocaleDateString('he-IL') : '';
    },
  },
  mounted() {
    this.load(false);
    this.clock = setInterval(() => { this.now = Date.now(); }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.clock);
  },
};
</script>

<style scoped>
.sas {
  --ink: #0d2c6d;
  --ink-soft: #3d5488;
  --gold: #ffb91a;
  --paper: #f5f7fb;
  --line: #e3e8f2;
  --muted: #6b7690;
  --open: #2e9e5b;
  --scheduled: #e0920b;
  --closed: #8a93a6;
  font-family: 'Heebo', sans-serif;
  color: var(--ink);
  padding: 28px 32px 48px;
  width: 100%;
  max-width: 1280px;
  min-width: 0; /* רוחב קבוע להורה: בלי זה הגרף השבועי מרחיב את כל הדף במובייל */
  box-sizing: border-box;
  margin: 0 auto;
}

/* ---------- loading / messages ---------- */
.sas-loading { display: grid; gap: 24px; }
.sas-loading__hero { height: 260px; border-radius: 20px; overflow: hidden; }
.sas-message {
  display: grid;
  justify-items: start;
  gap: 16px;
  padding: 40px 0;
  font-size: 17px;
  color: var(--ink-soft);
  max-width: 60ch;
}
.sas-message p { margin: 0; }

/* ---------- hero: the keypad ---------- */
.sas-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 48px;
  padding: 36px 40px;
  background: var(--ink);
  color: #fff;
  border-radius: 24px;
}
.sas-hero__title {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: #b9c6e4;
}
.sas-hero__headline {
  margin: 10px 0 0;
  font-size: clamp(28px, 3.4vw, 44px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.02em;
  max-width: 18ch;
}
.sas-hero__sub {
  margin: 14px 0 0;
  font-size: 16px;
  color: #d4dcf0;
  max-width: 46ch;
  line-height: 1.5;
}
.sas-hero__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 22px;
  font-size: 13px;
  color: #9fb0d6;
}

.sas-pad {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(3, 76px);
  gap: 12px;
  direction: ltr; /* לוח מקשים של טלפון: 1 משמאל */
}
.sas-pad__key {
  --fill: 0;
  position: relative;
  height: 68px;
  border-radius: 18px;
  background: rgba(255, 255, 255, .07);
  border: 1px solid rgba(255, 255, 255, .16);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
.sas-pad__key::before {
  content: '';
  position: absolute;
  inset: auto 0 0 0;
  height: calc(var(--fill) * 100%);
  background: rgba(255, 255, 255, .22);
  transition: height .7s cubic-bezier(.2, .8, .2, 1) var(--delay, 0ms);
}
.sas-pad__key.is-lead { border-color: var(--gold); }
.sas-pad__key.is-lead::before { background: var(--gold); }
.sas-pad__digit,
.sas-pad__share { position: relative; }
.sas-pad__digit { font-size: 24px; font-weight: 800; }
.sas-pad__share {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #d4dcf0;
  min-height: 12px;
}
.sas-pad__key.is-lead .sas-pad__digit,
.sas-pad__key.is-lead .sas-pad__share { color: var(--ink); }
.sas-pad__key.is-empty .sas-pad__digit { color: #8ea0c9; }
.sas-pad__key.is-off { background: transparent; border-style: dashed; border-color: rgba(255, 255, 255, .12); }
.sas-pad__key.is-off .sas-pad__digit { color: #5f74a8; font-weight: 500; }

/* ---------- numbers strip ---------- */
.sas-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin: 28px 0 8px;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.sas-strip__item {
  padding: 18px 20px;
  border-inline-start: 1px solid var(--line);
}
.sas-strip__item:first-child { border-inline-start: 0; padding-inline-start: 0; }
.sas-strip dt { font-size: 13px; color: var(--muted); }
.sas-strip dd { margin: 6px 0 0; display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.sas-strip__value {
  font-size: 28px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.sas-strip__note { font-size: 13px; color: var(--ink-soft); }

/* ---------- blocks ---------- */
.sas-block { margin-top: 40px; min-width: 0; }
.sas-block__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.sas-block__head h3 { margin: 0; font-size: 19px; font-weight: 700; }
.sas-none { margin: 0; color: var(--muted); font-size: 15px; }

/* ---------- weekly ---------- */
.sas-legend { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--muted); }
.sas-legend__bar { width: 10px; height: 14px; border-radius: 3px; background: var(--ink); }
.sas-legend__num {
  margin-inline-start: 10px;
  width: 18px;
  height: 14px;
  border: 1px solid #cfd7e6;
  border-radius: 5px;
}
.sas-weeks {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 10px;
  max-width: 100%;
  overflow-x: auto; /* RTL: השבוע הישן מימין, הנוכחי משמאל */
}
.sas-week {
  display: grid;
  grid-template-rows: 18px 160px auto auto;
  justify-items: center;
  gap: 6px;
  min-width: 36px;
}
.sas-week__votes { font-size: 12px; font-weight: 700; color: var(--ink-soft); font-variant-numeric: tabular-nums; }
.sas-week__track {
  width: 100%;
  max-width: 44px;
  height: 160px;
  display: flex;
  align-items: flex-end;
  background: var(--paper);
  border-radius: 8px;
  overflow: hidden;
}
.sas-week__bar { width: 100%; background: var(--ink-soft); border-radius: 8px 8px 0 0; }
.sas-week.is-current .sas-week__bar { background: var(--ink); }
.sas-week__new {
  font-size: 12px;
  font-weight: 700;
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 1px 6px;
  font-variant-numeric: tabular-nums;
}
.sas-week__new.is-zero { color: #b3bccd; }
.sas-week__date { font-size: 12px; color: var(--muted); font-variant-numeric: tabular-nums; }

/* ---------- leaderboards ---------- */
.sas-boards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 48px; }
.sas-board { list-style: none; padding: 0; margin: 0; }
.sas-board__row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1.3fr) minmax(0, 1fr) auto;
  grid-template-areas: "rank who track value" "rank who track aside";
  align-items: center;
  column-gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.sas-board__rank { grid-area: rank; font-size: 14px; font-weight: 700; color: var(--muted); font-variant-numeric: tabular-nums; }
.sas-board__row:first-child .sas-board__rank { color: var(--ink); }
.sas-board__who { grid-area: who; display: grid; min-width: 0; }
.sas-board__name { font-weight: 700; font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sas-board__line { font-size: 12px; color: var(--muted); font-variant-numeric: tabular-nums; }
.sas-board__track { grid-area: track; height: 8px; background: var(--paper); border-radius: 99px; overflow: hidden; }
.sas-board__bar { display: block; height: 100%; background: var(--ink-soft); border-radius: 99px; }
.sas-board__row:first-child .sas-board__bar { background: var(--ink); }
.sas-board__value { grid-area: value; font-weight: 800; font-size: 16px; text-align: end; font-variant-numeric: tabular-nums; }
.sas-board__value small { font-size: 12px; font-weight: 500; color: var(--muted); }
.sas-board__aside { grid-area: aside; font-size: 12px; color: var(--muted); text-align: end; }

/* ---------- top surveys ---------- */
.sas-top { list-style: none; padding: 0; margin: 0; }
.sas-top__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 92px;
  align-items: center;
  gap: 20px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}
.sas-top__q { margin: 0; font-size: 16px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sas-top__meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; margin: 4px 0 0; font-size: 13px; color: var(--muted); }
.sas-top__line { font-variant-numeric: tabular-nums; }
.sas-pill { font-weight: 700; }
.sas-pill.is-open { color: var(--open); }
.sas-pill.is-processing { color: var(--scheduled); }
.sas-pill.is-closed { color: var(--closed); }
.sas-top__keys { display: flex; gap: 4px; direction: ltr; }
.sas-mini {
  --fill: 0;
  width: 26px;
  height: 30px;
  border-radius: 7px;
  border: 1px solid #cfd7e6;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 800;
  background:
    linear-gradient(to top, rgba(13, 44, 109, .16) calc(var(--fill) * 100%), transparent calc(var(--fill) * 100%)),
    #fff;
}
.sas-mini.is-lead {
  border-color: var(--gold);
  background:
    linear-gradient(to top, rgba(255, 185, 26, .55) calc(var(--fill) * 100%), transparent calc(var(--fill) * 100%)),
    #fff;
}
.sas-top__votes { text-align: end; font-size: 22px; font-weight: 800; line-height: 1.1; font-variant-numeric: tabular-nums; }
.sas-top__votes small { display: block; font-size: 12px; font-weight: 500; color: var(--muted); }

/* ---------- composition ---------- */
.sas-mix { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px 48px; }
.sas-mix__title { margin: 0 0 8px; font-size: 14px; font-weight: 700; }
.sas-split { display: flex; gap: 3px; height: 12px; border-radius: 99px; overflow: hidden; background: var(--paper); }
.sas-split__part { flex-basis: 0; }
.sas-split__part.is-open, .sas-mix__dot.is-open { background: var(--open); }
.sas-split__part.is-scheduled, .sas-mix__dot.is-scheduled { background: var(--scheduled); }
.sas-split__part.is-closed, .sas-mix__dot.is-closed { background: var(--closed); }
.sas-split__part.is-ink, .sas-mix__dot.is-ink { background: var(--ink); }
.sas-split__part.is-soft, .sas-mix__dot.is-soft { background: #9db0d8; }
.sas-mix__legend { display: flex; flex-wrap: wrap; gap: 4px 16px; margin: 8px 0 0; font-size: 13px; color: var(--ink-soft); }
.sas-mix__item { display: inline-flex; align-items: center; gap: 6px; }
.sas-mix__item b { color: var(--ink); font-variant-numeric: tabular-nums; }
.sas-mix__dot { width: 8px; height: 8px; border-radius: 50%; }

/* ---------- a11y ---------- */
.sas-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------- responsive ---------- */
@media (max-width: 1100px) {
  .sas-strip { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .sas-strip__item:nth-child(4) { border-inline-start: 0; padding-inline-start: 0; }
  .sas-strip__item:nth-child(n + 4) { border-top: 1px solid var(--line); }
}
@media (max-width: 960px) {
  .sas { padding: 20px 16px 40px; }
  .sas-hero { grid-template-columns: 1fr; gap: 28px; padding: 28px 24px; }
  .sas-pad { justify-self: center; }
  .sas-boards, .sas-mix { grid-template-columns: 1fr; }
  .sas-top__row { grid-template-columns: minmax(0, 1fr) 72px; }
  .sas-top__keys { grid-column: 1 / -1; grid-row: 2; }
}
@media (max-width: 600px) {
  .sas-pad { grid-template-columns: repeat(3, 64px); gap: 10px; }
  .sas-pad__key { height: 58px; }
  .sas-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .sas-strip__item,
  .sas-strip__item:nth-child(4) { border-inline-start: 0; padding-inline-start: 0; }
  .sas-strip__item:nth-child(n + 3) { border-top: 1px solid var(--line); }
  .sas-weeks { grid-template-columns: repeat(12, 36px); }
}
@media (prefers-reduced-motion: reduce) {
  .sas-pad__key::before { transition: none; }
}
</style>